import {
  Box,
  Input,
  type SxProps,
  Typography,
  useMediaQuery
} from '@mui/material'
import { includes, isEmpty, isNil, replace, split } from 'lodash/fp'
import React, { type ReactElement } from 'react'
import {
  type InputProps,
  type TextInputProps,
  useInput,
  useTranslate
} from 'react-admin'
import { useFormState, useFormContext, useWatch } from 'react-hook-form'
import {
  DatePicker,
  type DateView,
  LocalizationProvider,
  TimePicker
} from '@mui/x-date-pickers'
import ja from 'date-fns/locale/ja'
import {
  format,
  isValid,
  parseISO,
  startOfYear,
  startOfMonth
} from 'date-fns/fp'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import log from 'loglevel'
import { MuiFileInput } from 'mui-file-input'
import CloseIcon from '@mui/icons-material/Close'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

const ValidateError: React.FC<{
  errorMessage: string
}> = ({ errorMessage }) => {
  const translate = useTranslate()
  return (
    <Typography variant="subtitle2" color="red" sx={{ marginLeft: '1em' }}>
      {translate(errorMessage)}
    </Typography>
  )
}

export const TextInput: React.FC<
  TextInputProps & {
    source: string
    placeholder: string
    fields?: string
    validate?: any
    label?: string
  }
> = ({ source, placeholder, validate, fields, label, ...props }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const { field, isRequired } = useInput({ source, validate, ...props })
  const translate = useTranslate()
  const name = replace(/\.\d/, '')(field.name)
  const labelString = isNil(label)
    ? translate(`${fields as string}.${name}`)
    : label
  const { errors } = useFormState()
  let error: any = errors
  if (!isNil(error)) {
    for (const field of split('.')(source)) {
      error = error[field]
      if (isNil(error)) break
    }
  }
  return (
    <>
      <Typography
        variant="subtitle1"
        sx={{ marginLeft: isDesktop ? '0.50em' : '0.25em' }}>
        {labelString}
      </Typography>
      <Box
        border={1}
        sx={{
          height: '2em',
          display: 'flex',
          alignContent: 'center',
          marginTop: isDesktop ? '0.1em' : '0.25em',
          marginLeft: isDesktop ? '0.50em' : '0.25em',
          borderRadius: '10px',
          borderColor: '#c5c5c5'
        }}>
        <Input
          {...field}
          placeholder={placeholder}
          fullWidth
          disableUnderline
          sx={{ marginLeft: '1em', fontVariant: 'body1' }}
          required={isRequired}
        />
      </Box>
      {!isNil(error) && <ValidateError errorMessage={error.message} />}
    </>
  )
}

export const DateInput: React.FC<
  {
    source: string
    disableFuture?: boolean
    disablePast?: boolean
    fields?: string
    fullWidth?: boolean
    views?: DateView[]
    sx?: SxProps
    onChange?: (source: string, value: string) => void
    minDate?: Date | null | undefined
    maxDate?: Date | null | undefined
  } & InputProps
> = (props) => {
  const translate = useTranslate()
  const {
    source,
    fields,
    disableFuture,
    disablePast,
    fullWidth: fullWidthProps,
    views,
    onChange: onChangeUser,
    sx,
    minDate,
    maxDate
  } = props
  const { field, isRequired, ...rest } = useInput(props)
  const { value } = field
  const [dateValue, setDateValue] = React.useState<Date | null>(
    !isNil(value) && !isEmpty(value) ? parseISO(value) : null
  )
  const { setValue } = useFormContext()
  const { label: labelProps } = props
  const label =
    labelProps ??
    translate(`${fields as string}.${replace(/\.[0-9]+/g, '')(field.name)}`)
  React.useEffect(() => {
    setDateValue(!isNil(value) && !isEmpty(value) ? parseISO(value) : null)
  }, [value])
  const fullWidth = fullWidthProps ?? false
  return (
    <>
      {isNil(labelProps) && (
        <Typography variant="subtitle1">{label as string}</Typography>
      )}
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={ja}
        localeText={{
          cancelButtonLabel: 'キャンセル',
          okButtonLabel: '選択'
        }}>
        <DatePicker
          {...rest}
          value={dateValue}
          format="yyyy-MM-dd"
          onChange={(newValue: Date | null): void => {
            if (isValid(newValue)) {
              const adjusted = !isNil(newValue)
                ? !includes('day')(views)
                  ? !includes('month')(views)
                    ? startOfYear(newValue)
                    : startOfMonth(newValue)
                  : newValue
                : newValue
              const value = !isNil(adjusted)
                ? format('yyyy-MM-dd')(adjusted)
                : ''
              setDateValue(adjusted)
              setValue(source, value, {
                shouldDirty: true,
                shouldValidate: true
              })
              field.onChange(value)
              if (!isNil(onChangeUser)) onChangeUser(source, value)
            }
          }}
          minDate={minDate}
          maxDate={maxDate}
          slotProps={{
            field: {
              clearable: true,
              onClear: (): void => {
                setValue(source, null, {
                  shouldDirty: true,
                  shouldValidate: true
                })
                setDateValue(null)
                field.onChange(null)
                if (!isNil(onChangeUser)) onChangeUser(source, null)
              },
              format: !includes('day')(views)
                ? !includes('month')(views)
                  ? 'yyyy年'
                  : 'yyyy年M月'
                : 'yyyy年M月d日'
            },
            textField: {
              label: labelProps,
              required: isRequired,
              sx: {
                ...sx,
                borderRadius: '10px',
                borderColor: '#c5c5c5'
              },
              variant: 'outlined',
              fullWidth,
              size: 'medium',
              inputProps: { style: { fontVariant: 'body1' } },
              InputLabelProps: { disabled: true },
              hiddenLabel: true
            },
            toolbar: {
              hidden: true
            }
          }}
          views={views ?? ['year', 'month', 'day']}
          disableFuture={!isNil(disableFuture) ? disableFuture : false}
          disablePast={!isNil(disablePast) ? disablePast : false}
        />
      </LocalizationProvider>
    </>
  )
}
export const TimeInput: React.FC<
  {
    source: string
    fields?: string
    fullWidth?: boolean
    sx?: SxProps
  } & InputProps
> = (props) => {
  const translate = useTranslate()
  const { source, fields, fullWidth: fullWidthProps, sx } = props
  const { field, isRequired } = useInput(props)
  const { value } = field
  const [dateValue, setDateValue] = React.useState<Date | null>(
    !isNil(value) && !isEmpty(value)
      ? parseISO(`1970-01-01T${value as string}`)
      : null
  )
  const { setValue } = useFormContext()
  const { label: labelProps } = props
  const label =
    labelProps ??
    translate(
      `${fields as string} as string.${replace(/\.[0-9]+/g, '')(field.name)}`
    )
  React.useEffect(() => {
    setDateValue(
      !isNil(value) && !isEmpty(value)
        ? parseISO(`1970-01-01T${value as string}`)
        : null
    )
  }, [value])
  const fullWidth = fullWidthProps ?? false
  return (
    <>
      {isNil(labelProps) && (
        <Typography variant="subtitle1">{label as string}</Typography>
      )}
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={ja}
        localeText={{
          cancelButtonLabel: 'キャンセル',
          okButtonLabel: '選択'
        }}>
        <TimePicker
          {...field}
          value={dateValue}
          format="HH:mm"
          onChange={(newValue: Date | null): void => {
            if (isValid(newValue)) {
              const value = !isNil(newValue) ? format('HH:mm')(newValue) : ''
              setDateValue(newValue)
              setValue(source, value, {
                shouldDirty: true,
                shouldValidate: true
              })
              field.onChange(value)
            }
          }}
          slotProps={{
            field: {
              clearable: true,
              onClear: (): void => {
                setValue(source, null, {
                  shouldDirty: true,
                  shouldValidate: true
                })
                setDateValue(null)
                field.onChange(null)
              },
              format: 'HH:mm'
            },
            textField: {
              label: labelProps,
              required: isRequired,
              sx: {
                ...sx,
                borderRadius: '10px',
                borderColor: '#c5c5c5'
              },
              variant: 'outlined',
              fullWidth,
              size: 'medium',
              inputProps: { style: { fontVariant: 'body1' } },
              InputLabelProps: { disabled: true },
              hiddenLabel: true
            },
            toolbar: {
              hidden: true
            }
          }}
          views={['hours', 'minutes']}
          ampm={false}
          minutesStep={30}
        />
      </LocalizationProvider>
    </>
  )
}
export const useScroll = (obj?: any): React.RefObject<HTMLDivElement> => {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  React.useLayoutEffect(() => {
    scrollRef?.current?.scrollIntoView({
      block: 'center'
    })
  }, [])
  React.useEffect(() => {
    if (!isNil(obj)) {
      scrollRef?.current?.scrollIntoView({
        block: 'center'
      })
    }
  }, [obj])
  return scrollRef
}
export const ScrollTo: React.FC<{
  setScroll: (ref: React.RefObject<HTMLDivElement>) => void
  renderItem: (props: {
    scrollRef: React.RefObject<HTMLDivElement>
  }) => ReactElement
}> = ({ setScroll, renderItem }) => {
  const scrollRef = useScroll()
  const item = renderItem({ scrollRef })
  React.useEffect(() => {
    setScroll(scrollRef)
  })
  return (
    <div>
      {item}
      <div ref={scrollRef} />
    </div>
  )
}
export type ScrollProps = {
  scrollRef: React.RefObject<HTMLDivElement>
}
export const FileInput: React.FC<
  {
    placeholder: string
    variant: 'filled' | 'outlined' | 'standard' | undefined
    inputProps: any
  } & InputProps
> = ({ placeholder, variant, inputProps, ...props }) => {
  const { isRequired } = useInput(props)
  const { source } = props
  const { setValue, getValues } = useFormContext()
  const [file, setFile] = React.useState<File | null>(
    getValues()[source] ?? null
  )
  const handleChange = (newFile: File | null): void => {
    setFile(newFile)
    setValue(source, newFile, { shouldDirty: true, shouldValidate: true })
  }
  const sourceValue = useWatch({ name: source })
  React.useEffect(() => {
    if (file !== sourceValue) setFile(sourceValue ?? null)
  }, [sourceValue])
  return (
    <MuiFileInput
      {...props}
      value={file}
      placeholder={isRequired ? `${placeholder}*` : placeholder}
      variant={variant}
      inputProps={inputProps}
      onChange={handleChange}
      required={isRequired}
      clearIconButtonProps={{
        title: 'Remove',
        children: <CloseIcon fontSize="small" />
      }}
    />
  )
}
